import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ClassificacaoCode } from '@/types/api';

/**
 * Hooks de Classificações (CID-10 + CIAP-2).
 *
 * PROMPT 06e: autocomplete no front-end consome os endpoints do back-end:
 *   GET /api/classificacoes/cid10?q=&limit=
 *   GET /api/classificacoes/ciap2?q=&limit=
 *   GET /api/classificacoes/cid10/:code
 *   GET /api/classificacoes/ciap2/:code
 *
 * O `enabled` evita requests com query vazia (mínimo 1 char no back).
 * `keepPreviousData` mantém a lista anterior visível enquanto a nova carrega.
 */

const MIN_QUERY_LENGTH = 1;
const DEFAULT_LIMIT = 20;

async function searchCid10(q: string, limit = DEFAULT_LIMIT) {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return api.get<{ count: number; items: ClassificacaoCode[] }>(
    `/api/classificacoes/cid10?${params.toString()}`,
  );
}

async function searchCiap2(q: string, limit = DEFAULT_LIMIT) {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return api.get<{ count: number; items: ClassificacaoCode[] }>(
    `/api/classificacoes/ciap2?${params.toString()}`,
  );
}

async function fetchCid10Detail(code: string) {
  return api.get<ClassificacaoCode>(
    `/api/classificacoes/cid10/${encodeURIComponent(code)}`,
  );
}

async function fetchCiap2Detail(code: string) {
  return api.get<ClassificacaoCode>(
    `/api/classificacoes/ciap2/${encodeURIComponent(code)}`,
  );
}

export function useSearchCid10(query: string, limit?: number) {
  return useQuery({
    queryKey: ['classificacoes', 'cid10', 'search', query, limit ?? DEFAULT_LIMIT],
    queryFn: () => searchCid10(query, limit),
    enabled: query.trim().length >= MIN_QUERY_LENGTH,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useSearchCiap2(query: string, limit?: number) {
  return useQuery({
    queryKey: ['classificacoes', 'ciap2', 'search', query, limit ?? DEFAULT_LIMIT],
    queryFn: () => searchCiap2(query, limit),
    enabled: query.trim().length >= MIN_QUERY_LENGTH,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useCid10Detail(code: string | null | undefined) {
  return useQuery({
    queryKey: ['classificacoes', 'cid10', 'detail', code],
    queryFn: () => fetchCid10Detail(code as string),
    enabled: Boolean(code && code.trim().length > 0),
    staleTime: 5 * 60_000, // 5 min — descrição não muda
  });
}

export function useCiap2Detail(code: string | null | undefined) {
  return useQuery({
    queryKey: ['classificacoes', 'ciap2', 'detail', code],
    queryFn: () => fetchCiap2Detail(code as string),
    enabled: Boolean(code && code.trim().length > 0),
    staleTime: 5 * 60_000,
  });
}
